import { Add } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    Grid,
    TextField,
    useTheme,
} from "@mui/material";
import { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import { Global_CTX } from "../../contexts/global-ctx";
import {
    INew_User,
    User_Service,
    User_Verification_Status,
} from "../../services/user/user-service";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import UserContext, { User_Type } from "../UserContext";

const initial_user: INew_User = {
    name: "",
    verified: User_Verification_Status.Pending,
    password: "",
    branch: 0,
    group: 0,
    privilege: 0,
    disabled: 0,
};

const initial_filter = { org_code: "", org_api_key: "" };

export default function Create_User_Form() {
    const [user, set_user] = useState(initial_user);
    const [errors, setErrors] = useState<{ pin?: string }>({});
    const [existing_pins, set_existing_pins] = useState<number>();
    const [filter, set_filter] = useState(initial_filter);
    const { feedback, set_feedback, set_refetch_counter } =
        useContext(Global_CTX);
    const { user: ctx_user } = useContext(UserContext);

    const theme = useTheme();

    const handle_change = (ev: ChangeEvent<HTMLInputElement>) => {
        const value = ev.target.value;
        set_user({
            ...user,
            [ev.target.name]: value,
        });
    };

    const getExistingPins = async () => {
        const [data, error] =
            await User_Service.get_instance().get_existing_pin(
                filter.org_code,
                { branch: user.branch }
            );

        if (data) {
            set_existing_pins(data);
        }
    };

    useEffect(() => {
        if (user.branch) {
            getExistingPins();
        }
    }, [user.branch]);

    async function handle_submit(ev: FormEvent) {
        ev.preventDefault();

        set_feedback({ ...feedback, loading: true });

        const [data, error] = await User_Service.get_instance().create_user(
            user,
            filter.org_code,
            filter.org_api_key
        );

        if (error) {
            set_feedback({
                loading: false,
                message: { title: error.message },
                severity: "error",
                show: true,
            });

            return;
        }

        set_feedback({
            loading: false,
            message: { title: data?.message ?? "" },
            severity: "success",
            show: true,
        });
        set_refetch_counter((c: any) => c + 1);
    }

    useEffect(() => {
        if (ctx_user?.user_type === User_Type.Admin) {
            set_filter((f) => ({
                ...f,
                org_code: ctx_user.organization.code,
                org_api_key: ctx_user.organization.apiKey[0].key,
            }));
        }
    }, [ctx_user?.user_type]);

    console.log(filter);

    return (
        <form onSubmit={handle_submit}>
            <Grid container spacing={1}>
                <Grid item md={4} xl={3}>
                    <TextField
                        label="Name"
                        fullWidth
                        value={user.name}
                        name="name"
                        onChange={handle_change}
                        required
                    />
                </Grid>

                {/* <Grid item md={4} xl={3}>
                    <Autocomplete
                        label="Status"
                        labelKey="label"
                        setOutput={(
                            status: { label: keyof typeof Device_Status } | ""
                        ) =>
                            set_user((curr) => ({
                                ...curr,
                                status: status
                                    ? Device_Status[status.label]
                                    : ("" as any),
                            }))
                        }
                        defaultOptions={Object.keys(Device_Status).map(
                            (label, id) => ({
                                label,
                                id,
                            })
                        )}
                    />
                </Grid> */}

                {ctx_user?.user_type === User_Type.Superadmin && (
                    <Grid item md={4} xl={3}>
                        <Autocomplete
                            key={`org-input`}
                            api="/organization"
                            label="Organization"
                            labelKey="name"
                            setOutput={(org) => {
                                set_filter((curr) => ({
                                    ...curr,
                                    org_code: org ? org.code : "",
                                }));
                            }}
                        />
                    </Grid>
                )}
                <Grid item md={4} xl={3}>
                    <Autocomplete
                        key={`branch-input`}
                        label="Branch"
                        labelKey="name"
                        setOutput={(branch) =>
                            set_user((curr) => ({
                                ...curr,
                                branch: branch?.id ?? "",
                            }))
                        }
                        api="/organization/branch"
                        apiParams={{
                            organization: filter.org_code,
                        }}
                        request_headers={{
                            "x-org": filter.org_code,
                            authorization: `Bearer ${filter.org_api_key}`,
                        }}
                    />
                </Grid>

                <Grid item md={4} xl={3}>
                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        value={user.password}
                        name="password"
                        onChange={handle_change}
                        required
                    />
                </Grid>

                {/* <Grid item md={4} xl={3}>
                    <TextField
                        label="PIN"
                        type="number"
                        fullWidth
                        value={user.pin}
                        name="pin"
                        onChange={handle_change}
                        required
                        helperText={
                            existing_pins !== undefined &&
                            `Existing PINs: 0 - ${existing_pins}`
                        }
                    />
                </Grid> */}
                <Grid item md={4} xl={3}>
                    <TextField
                        label="PIN"
                        type="number"
                        fullWidth
                        value={user.pin}
                        name="pin"
                        onChange={(e) => {
                            const value = Number(e.target.value);

                            if (
                                existing_pins !== undefined &&
                                value <= existing_pins
                            ) {
                                setErrors((prev) => ({
                                    ...prev,
                                    pin: `PIN must be greater than ${existing_pins}`,
                                }));
                            } else {
                                setErrors((prev) => ({
                                    ...prev,
                                    pin: "",
                                }));
                                handle_change(e as any);
                            }
                        }}
                        required
                        error={Boolean(errors?.pin)}
                        helperText={
                            errors?.pin ||
                            (existing_pins !== undefined &&
                                `Existing PINs: 0 - ${existing_pins}`)
                        }
                        inputProps={{
                            min: existing_pins ? existing_pins + 1 : 0,
                        }}
                    />
                </Grid>

                <Grid item md={4} xl={3}>
                    <TextField
                        label="Group"
                        fullWidth
                        value={user.group}
                        InputProps={{ type: "number" }}
                        name="group"
                        onChange={handle_change}
                        required
                    />
                </Grid>

                <Grid item md={4} xl={3}>
                    <TextField
                        label="Card"
                        fullWidth
                        value={user.card}
                        name="card"
                        onChange={handle_change}
                    />
                </Grid>

                <Grid item md={4} xl={3}>
                    <Button
                        sx={{ height: "100%" }}
                        type="submit"
                        variant="contained"
                        disabled={feedback.loading}
                        startIcon={
                            feedback.loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <Add />
                            )
                        }
                    >
                        {feedback.loading ? "Creating User..." : "Create User"}

                        {/* {!updateMode
              ? feedback.loading
                ? "Adding session..."
                : "Add session"
              : feedback.loading
                ? "updating session..."
                : "update session"} */}
                    </Button>
                </Grid>
            </Grid>

            {/* <TextField
        label="Address"
        // multiline
        // minRows={3}
        fullWidth
        value={device.address}
        name="address"
        onChange={handle_change}
        sx={{ flex: 1 }}
        required
      /> */}

            {/* <Feedback /> */}
        </form>
    );
}
